import React from "react";
import { Timer as ClockIcon } from "lucide-react";

/**
 * Props:
 *  - recentActivity: Array<{
 *      description: string,
 *      timestamp: string,
 *      last_updated_field?: string
 *    }>
 */
const RecentActivity = ({ title = "", activities = [] }) => {

  const [isDark, setIsDark] = React.useState(
    document.documentElement.classList.contains('dark'),
  );

  React.useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);
  const formatLocalTime = (timestamp) => {
    if (!timestamp) return "";
    // Ensure timestamp is treated as UTC if it doesn't specify timezone
    const timeString = timestamp.endsWith("Z") ? timestamp : `${timestamp}Z`;
    const date = new Date(timeString);
    return date.toLocaleString();
  };

  // Handle different data structures for activities
  let processedActivities = [];

  /* ---------------- Dummy Activity Data ---------------- */
  const dummyActivities = [
    {
      description: "5 candidates uploaded.",
      timestamp: new Date().toISOString(),
    },
    {
      description: "3 candidates sourced.",
      timestamp: new Date().toISOString(),
    },
    {
      description: "2 interviews scheduled.",
      timestamp: new Date().toISOString(),
    },
  ];
  
  if (Array.isArray(activities)) {
    processedActivities = activities;
  } else if (activities && typeof activities === 'object') {
    // Flatten the dictionary structure { others: [], uploads: [], ... }
    Object.entries(activities).forEach(([key, list]) => {
      if (Array.isArray(list)) {
        const items = list.map(item => {
          // Generate description for aggregated types to ensure accuracy with count
          if (['uploads', 'sourced', 'schedules'].includes(key) && typeof item.count === 'number') {
              let desc = "";
              if (key === 'uploads') desc = `${item.count} candidates uploaded.`;
              else if (key === 'sourced') desc = `${item.count} candidates sourced.`;
              else if (key === 'schedules') desc = `${item.count} interviews scheduled.`;
              
              return { ...item, description: desc };
          }
          return item;
        });
        processedActivities.push(...items);
      }
    });
  }

  // Sort by timestamp descending (newest first)
  const realActivities = [...processedActivities]
    .filter((item) => (item.count === undefined || item.count > 0) && item.description)
    .sort((a, b) => {
      const tA = a.timestamp || "";
      const tB = b.timestamp || "";
      const timeA = tA.endsWith("Z") ? tA : `${tA}Z`;
      const timeB = tB.endsWith("Z") ? tB : `${tB}Z`;
      return new Date(timeB) - new Date(timeA);
    });

const hasData = realActivities.length > 0;

const sortedActivities = hasData ? realActivities : dummyActivities;

  return (
    <div className="dark:bg-gradient-to-r from-[#232142]/10 to-[#7E64A3]/10 bg-bgSecondary rounded-xl p-6 text-black dark:text-white">
      <div className="flex items-center gap-3">
        <ClockIcon className="w-5 h-5 text-[#404040] dark:text-[#AEB9E1]" />
        <h2 className="text-[#404040] dark:text-[#AEB9E1] text-lg font-medium tracking-wide">
          {title}
        </h2>
      </div>

      {/* {!hasData && (
        <div className="pt-4 px-2">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm px-4 py-2 rounded-md border border-gray-200 dark:border-gray-700/50 shadow-sm w-fit">
            <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">
              No data to display
            </span>
          </div>
        </div>
      )} */}

      <div
        className={`relative max-h-[250px] overflow-y-auto pr-1`}
        style={{ 
          scrollbarWidth: 'thin', 
          scrollbarColor: `${isDark ? '#454448' : '#eceffb'} transparent` 
        }}

      >
        <style>{`
          .activity-scrollbar::-webkit-scrollbar { width: 3px; }
          .activity-scrollbar::-webkit-scrollbar-track { background: transparent; }
          .activity-scrollbar::-webkit-scrollbar-thumb { background: #9CA3AF; border-radius: 999px; }
        `}</style>

        {!hasData && (
          <div className="absolute inset-0 flex items-center justify-center z-10 backdrop-blur-[2px]">
            <div className="bg-white/90 dark:bg-gray-800/90 px-6 py-3 rounded-lg border border-gray-300 dark:border-gray-700 shadow-md">
              <span className="text-gray-500 dark:text-gray-400 text-base font-semibold tracking-wide">
                No data to display
              </span>
            </div>
          </div>
        )}

      {sortedActivities.map((item, index) => (
        <div key={index} className="text-sm font-light pt-4 px-2 text-[#404040] dark:text-[#A8A8A8]">
          <p>
            {item.description}
          </p>

          <p className="dark:text-[#737373B2] text-[#808080]"
            style={{
              fontSize: "11px",
              opacity: 0.6,
              marginTop: "2px",
            }}
          >
            {formatLocalTime(item.timestamp)}
          </p>

          {index !== sortedActivities.length - 1 && (
            <div
              style={{
                height: "1px",
                background: "rgba(255,255,255,0.08)",
                marginTop: "10px",
              }}
            />
          )}
        </div>
      ))}
      </div>
    </div>
  );
};

export default RecentActivity;